import { useEffect, useMemo, useRef, useState } from 'react';
import { useData } from 'vike-react/useData';
import { usePageContext } from 'vike-react/usePageContext';
import type {
  CompanyProfileNotFoundReason,
  CompanyProfilePageData,
  CompanyProfileRouteData,
} from '../types/company-profile-page';
import type { CompanyProfileLocale } from '../utils/seo/company-profile-alternate-urls';
import { logCompanyProfileClientNav } from '../utils/company/company-profile-client-nav-log';
import { getVikeSerializedPageData } from '../utils/vike/get-vike-serialized-page-data';
import {
  readCompanyProfileRouteDataFromVikePageContextScript,
  resolveCompanyProfileRouteData,
} from '../utils/company/resolve-company-profile-page-data';
import { resolveCompanyProfileRouteSlug } from '../utils/company/resolve-company-profile-route-slug';
import { resolvePageLocale } from '../utils/routing/resolve-page-locale';

type OkData = CompanyProfilePageData;

function collectRouteCandidates(
  dataFromHook: CompanyProfileRouteData | undefined,
  contextData: CompanyProfileRouteData | undefined,
  expectedSlug: string | null,
  apiPayload: CompanyProfileRouteData | undefined,
): Array<CompanyProfileRouteData | OkData | null | undefined> {
  return [
    dataFromHook,
    contextData,
    getVikeSerializedPageData<CompanyProfileRouteData>(),
    readCompanyProfileRouteDataFromVikePageContextScript(expectedSlug),
    apiPayload,
  ];
}

/**
 * Resolves company profile data across SSR, Vike client navigation, and a same-origin API fallback
 * when serialized context is stale or useData() is delayed.
 */
export function useCompanyProfilePageData(): {
  data: OkData | undefined;
  /** True while we have a route slug but no matching payload yet (client fetch may be in flight). */
  isAwaitingData: boolean;
  /** Client fetch completed with 404 or error (no usable payload for this slug). */
  isNotFound: boolean;
  notFoundReason: CompanyProfileNotFoundReason | undefined;
} {
  const dataFromHook = useData<CompanyProfileRouteData>();
  const pageContext = usePageContext();
  const expectedSlug = resolveCompanyProfileRouteSlug(pageContext);
  const contextData = (pageContext as { data?: CompanyProfileRouteData } | undefined)?.data;

  const locale = resolvePageLocale({
    locale: pageContext?.locale as string | undefined,
    urlPathname:
      (pageContext?.urlPathname as string | undefined) ??
      (pageContext as { urlParsed?: { pathname?: string } })?.urlParsed?.pathname,
  }) as CompanyProfileLocale;

  const [apiPayload, setApiPayload] = useState<CompanyProfileRouteData | undefined>(undefined);
  const [clientFetchMiss, setClientFetchMiss] = useState(false);
  const apiPayloadRef = useRef(apiPayload);
  apiPayloadRef.current = apiPayload;

  useEffect(() => {
    setApiPayload(undefined);
    setClientFetchMiss(false);
    logCompanyProfileClientNav('route slug or locale changed; cleared client API cache', {
      expectedSlug,
      locale,
    });
  }, [expectedSlug, locale]);

  useEffect(() => {
    if (typeof window === 'undefined' || !expectedSlug) return;

    if (
      resolveCompanyProfileRouteData(
        collectRouteCandidates(dataFromHook, contextData, expectedSlug, apiPayloadRef.current),
        expectedSlug,
      )
    ) {
      logCompanyProfileClientNav('skip /api/company-profile — already have matching payload', {
        expectedSlug,
      });
      return;
    }

    const slugForRequest = expectedSlug;
    const controller = new AbortController();

    const url = `/api/company-profile?objectID=${encodeURIComponent(slugForRequest)}&locale=${encodeURIComponent(locale)}`;
    logCompanyProfileClientNav('fetch /api/company-profile', { url, slugForRequest, locale });
    void (async () => {
      try {
        const res = await fetch(url, {
          signal: controller.signal,
          headers: { 'user-agent': 'jobcrawls-internal/company-profile' },
        });
        logCompanyProfileClientNav('fetch /api/company-profile response', {
          slugForRequest,
          status: res.status,
          ok: res.ok,
        });
        let json: CompanyProfileRouteData | null = null;
        try {
          json = (await res.json()) as CompanyProfileRouteData;
        } catch {
          json = null;
        }
        if (json && typeof json === 'object' && json.notFound === true && json.routeSlug === slugForRequest) {
          setApiPayload(json);
          return;
        }
        if (json && typeof json === 'object' && json.notFound === false && json.company?.objectID) {
          const resolved = resolveCompanyProfileRouteData([json], slugForRequest);
          if (resolved?.notFound === false) {
            logCompanyProfileClientNav('fetch OK — applying apiPayload', {
              objectID: json.company.objectID,
            });
            setApiPayload(resolved);
            return;
          }
        }
        setClientFetchMiss(true);
      } catch (err) {
        if (controller.signal.aborted) {
          logCompanyProfileClientNav('fetch aborted (navigation or deps)', { slugForRequest });
        } else {
          logCompanyProfileClientNav('fetch error (network or parse)', {
            slugForRequest,
            error: err instanceof Error ? err.message : String(err),
          });
          setClientFetchMiss(true);
        }
      }
    })();

    return () => controller.abort();
  }, [contextData, dataFromHook, expectedSlug, locale]);

  const resolvedRoute = useMemo(
    () =>
      resolveCompanyProfileRouteData(
        collectRouteCandidates(dataFromHook, contextData, expectedSlug, apiPayload),
        expectedSlug,
      ),
    [apiPayload, contextData, dataFromHook, expectedSlug],
  );

  const data: OkData | undefined =
    resolvedRoute && resolvedRoute.notFound === false
      ? (() => {
          const { notFound: _n, ...rest } = resolvedRoute;
          return rest;
        })()
      : undefined;

  const explicitNotFound = resolvedRoute?.notFound === true;
  const isNotFound =
    Boolean(expectedSlug) && (explicitNotFound || (resolvedRoute === undefined && clientFetchMiss));

  const notFoundReason: CompanyProfileNotFoundReason | undefined =
    resolvedRoute?.notFound === true ? resolvedRoute.reason : undefined;

  const isAwaitingData =
    Boolean(expectedSlug) && resolvedRoute === undefined && !clientFetchMiss && !explicitNotFound;

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const winData = (window as { __VITE_PAGE_CONTEXT__?: { data?: { company?: { objectID?: string } } } })
      .__VITE_PAGE_CONTEXT__?.data;
    logCompanyProfileClientNav('state snapshot', {
      pathname: window.location.pathname,
      expectedSlug,
      locale,
      hasUseData: Boolean(dataFromHook),
      useDataObjectID:
        dataFromHook && dataFromHook.notFound === false ? (dataFromHook.company?.objectID ?? null) : null,
      hasPageContextData: Boolean(contextData),
      pageContextDataObjectID:
        contextData && contextData.notFound === false ? (contextData.company?.objectID ?? null) : null,
      windowSerializedObjectID:
        winData && typeof winData === 'object' && winData.company
          ? ((winData.company as { objectID?: string }).objectID ?? null)
          : null,
      scriptTagResolved: Boolean(readCompanyProfileRouteDataFromVikePageContextScript(expectedSlug)),
      apiPayloadObjectID:
        apiPayload && apiPayload.notFound === false ? (apiPayload.company?.objectID ?? null) : null,
      resolvedObjectID: data?.company?.objectID ?? null,
      isAwaitingData,
      isNotFound,
      clientFetchMiss,
    });
  }, [
    apiPayload,
    clientFetchMiss,
    contextData,
    data,
    dataFromHook,
    expectedSlug,
    isAwaitingData,
    isNotFound,
    locale,
  ]);

  return { data, isAwaitingData, isNotFound, notFoundReason };
}
